import { FileItem } from '../core/services/file-manager.service';

// Artifact Types and Interfaces
export enum ArtifactType {
    POSTER = 'POSTER',
    AUDIO_BOOK = 'AUDIO_BOOK',
    AUDIO_PLAYLIST = 'AUDIO_PLAYLIST',
    PDF = 'PDF'
}

export enum AccessStatus {
    ACTIVE = 'ACTIVE',
    EXPIRED = 'EXPIRED',
    REVOKED = 'REVOKED'
}

export enum ArtifactStatus {
    DRAFT = 'DRAFT',
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    ARCHIVED = 'ARCHIVED'
}

export interface Artifact {
    id: string;
    name: string;
    description: string;
    status: ArtifactStatus;
    type: ArtifactType;
    base_price: number;
    has_preview: boolean;
    preview_url?: string;
    thumbnail_url?: string;
    preview_content_id?: string;
    thumbnail_content_id?: string;
    preview_content?: FileItem;
    thumbnail_content?: FileItem;
    version_control: {
        current_version: string;
        version_history: {
            version: string;
            changes: string;
            created_at: Date;
            created_by: string;
        }[];
        is_latest: boolean;
    };
    drm_settings: {
        encryption_enabled: boolean;
        watermark_enabled: boolean;
        max_downloads: number;
        concurrent_sessions: number;
        allowed_devices: number;
        ip_restrictions?: string[];
        download_expiry_hours?: number;
    };
    content_urls?: Record<string, any>;
    encryption?: {
        key: string;
        iv: string;
        algorithm: string;
    };
    watermark?: {
        text: string;
        position: string;
        opacity: number;
        color: string;
    };
    access_control?: {
        allowed_ips: string[];
        allowed_domains: string[];
        max_downloads: number;
        expiry_date: Date;
    };
    metadata: {
        duration?: number;
        page_count?: number;
        size?: string;
        space_config?: any;
    };
    remarks?: string;
    is_deleted: boolean;
    created_by?: string;
    created_on: Date;
    last_modified_by?: string;
    last_modified_on: Date;
    category: {
        id: string;
        name: string;
    };
    tracks: ArtifactTrack[];
}

export interface ArtifactVersion {
    id: string;
    artifact_id: string;
    version: string;
    change_log: string;
    content_urls: {
        original: string;
        encrypted?: string;
        watermarked?: string;
    };
    metadata: {
        size: number;
        hash: string;
        mime_type: string;
    };
    remarks?: string;
    is_deleted: boolean;
    created_by?: string;
    created_on: Date;
    last_modified_by?: string;
    last_modified_on: Date;
}

export interface ArtifactTrackContent {
    id: string;
    title: string;
    thumbnail_url: string;
    preview_url: string;
    content_url: string;
    url: string;
    order_index: number;
    duration: number;
    playback_speed?: number;
    content_info: {
        file_size: number;
        mime_type: string;
        encoding?: string;
        quality?: string;
        thumbnail_url?: string;
    };
    metadata: {
        bitrate: number;
        chapter: string;
        description: string;
        format: string;
        language: string;
        sample_rate: number;
        section: string;
        tags: string[];
    };
    progress?: {
        last_position: number;
        last_accessed: Date;
        is_completed: boolean;
    };
}

export interface ArtifactTrack {
    id: string;
    artifact_id: string;
    title: string;
    duration?: number;
    track_url: string;
    track_content_id: string;
    preview_url?: string;
    thumbnail_url?: string;
    preview_content_id?: string;
    thumbnail_content_id?: string;
    preview_content?: FileItem;
    thumbnail_content?: FileItem;
    track_content?: FileItem;
    order_index: number;
    metadata?: {
        description?: string;
        chapter?: string;
        section?: string;
        tags?: string[];
        language?: string;
        format?: string;
        bitrate?: number;
        sample_rate?: number;
    };
    access_control?: {
        is_preview: boolean;
        preview_duration?: number;
        requires_authentication: boolean;
        allowed_roles?: string[];
    };
    progress_tracking?: {
        is_trackable: boolean;
        completion_threshold?: number;
        checkpoints?: {
            position: number;
            description: string;
        }[];
    };
    content_info?: {
        file_size: number;
        mime_type: string;
        encoding?: string;
        quality?: string;
        thumbnail_url?: string;
    };
    remarks?: string;
    is_deleted: boolean;
    created_by?: string;
    created_on: Date;
    last_modified_by?: string;
    last_modified_on: Date;
}

// Package Types and Interfaces
export interface VersionHistory {
    version: string;
    changes: string;
    created_at: Date;
    created_by: string;
}

export interface VersionControl {
    current_version: string;
    version_history: VersionHistory[];
}

export interface PricingTier {
    name: string;
    price: number;
    features: string[];
}

export interface AccessSettings {
    is_required: boolean;
    access_order?: number;
    unlock_condition?: string;
    preview_allowed: boolean;
}

export interface PackageArtifactMetadata {
    description?: string;
    features?: string[];
    requirements?: string[];
    recommended_order?: number;
}

export interface PackageSpecificSettings {
    is_featured: boolean;
    display_order: number;
    custom_title?: string;
    custom_description?: string;
}

export interface PackageArtifact {
    id: string;
    package_id: string;
    artifact_id: string;
    custom_price?: number;
    order_index: number;
    access_settings?: AccessSettings;
    metadata?: PackageArtifactMetadata;
    package_specific_settings?: PackageSpecificSettings;
    artifact: Artifact;
    created_on: Date;
    updated_on: Date;
}

export enum PackageStatus {
    DRAFT = 'DRAFT',
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    ARCHIVED = 'ARCHIVED'
}

export interface Package {
    id: string;
    name: string;
    description: string;
    status: PackageStatus;
    base_price: number;
    pricing_tiers: PricingTier[];
    access_expiry?: Date;
    is_active: boolean;
    version_control: VersionControl;
    artifacts: PackageArtifact[];
    created_on: Date;
    updated_on: Date;
    is_deleted: boolean;
}

// Order Types and Interfaces
export enum OrderType {
    INDIVIDUAL = 'INDIVIDUAL',
    PACKAGE = 'PACKAGE',
    GIFT = 'GIFT'
}

export enum OrderStatus {
    PENDING = 'PENDING',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    REFUNDED = 'REFUNDED',
    PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED'
}

export enum RefundStatus {
    PENDING = 'PENDING',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    REJECTED = 'REJECTED'
}

export enum PaymentStatus {
    PENDING = 'PENDING',
    ACTIVE = 'ACTIVE',
    PAST_DUE = 'PAST_DUE',
    UNPAID = 'UNPAID',
    CANCELED = 'CANCELED',
    NO_PAYMENT_REQUIRED = 'NO_PAYMENT_REQUIRED'
}

export enum ItemType {
    ARTIFACT = 'ARTIFACT',
    PACKAGE = 'PACKAGE'
}

export interface OrderItem {
    id: string;
    order_id: string;
    item_id: string;
    item_type: ItemType;
    price: number;
    metadata?: {
        size?: string;
        access_expiry?: Date;
        quantity?: number;
    };
}

export interface Order {
    id: string;
    user_id: string;
    type: OrderType;
    total_amount: number;
    currency: string;
    status: OrderStatus;
    refund_info?: {
        status: RefundStatus;
        amount?: number;
        reason?: string;
        processed_at?: Date;
        processed_by?: string;
        stripe_refund_id?: string;
    };
    payment_info: {
        currency: string;
        amount: number;
        status: PaymentStatus;
        stripe_payment_intent_id?: string;
        stripe_customer_id?: string;
        payment_method?: string;
        payment_date: Date;
    };
    gift_recipient_email?: string;
    gift_message?: string;
    items: OrderItem[];
    created_at: Date;
    updated_at: Date;
}

export interface OrderDetail {
    id: string,
    payment_info: {
        status: PaymentStatus,
        payment_date: Date
    },
    refund_info: {
        status: RefundStatus
    },
    order_number: string,
    status: OrderStatus,
    total_amount: number,
    currency: number,
    created_on: Date,
    items: {
        id: string,
        item_type: ItemType,
        quantity: number,
        price: number,
        total_amount: number,
        item: {
            id: string,
            name: string,
            description: string,
            type: ArtifactType,
            base_price: number,
            thumbnail_url: string
        }
    }[]
}


// Response Interfaces
export interface LibraryResponse {
    items: Artifact[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface LibraryItem {
    id: string;
    name: string;
    description: string;
    thumbnail_url: string;
    preview_url: string;
    access_expiry: Date;
    download_count: number;
    status: AccessStatus;
    type: ArtifactType;
    category?: {
        id: string;
        name: string;
    };
    progress: {
        current_track?: string;
        last_position?: number;
        completed_tracks?: string[];
        last_accessed?: Date;
    };
    tracks: ArtifactTrack[];
    track_progress?: {
        [key: string]: {
            last_position?: number;
            last_accessed?: Date;
            is_completed?: boolean;
        }
    }
}

// Response Interfaces
export interface ArtifactListResponse {
    items: FeaturedArtifact[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface PackageListResponse {
    items: Package[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface OrdersResponse {
    // version: string;
    items: OrderDetail[];
    total: number;
    // page: number;
    // limit: number;
    // totalPages: number;
}

// Admin API Order interfaces
export interface AdminOrderItem {
    id: string;
    artifact_id: string | null;
    package_id: string | null;
    quantity: number;
    price: number;
    total_price: number;
}

export interface AdminOrder {
    id: string;
    order_number: string;
    type: OrderType;
    total_amount: number;
    currency: string;
    status: OrderStatus;
    access_type: string;
    payment_info: {
        currency: string;
        amount: number;
        status: PaymentStatus;
        stripe_payment_intent_id?: string;
        stripe_customer_id?: string;
        payment_method?: string;
        payment_date: string;
    };
    refund_info: {
        status: RefundStatus;
        amount?: number;
        reason?: string;
        processed_at?: string;
        processed_by?: string;
        stripe_refund_id?: string;
    } | null;
    gift_recipient_email: string | null;
    gift_message: string | null;
    items: AdminOrderItem[];
    created_on: string;
    last_modified_on: string;
}

export interface AdminOrderListResponse {
    data: AdminOrder[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

export interface AdminOrderApiResponse {
    version: string;
    data: AdminOrderListResponse;
}

export interface OrderResponse {
    version: string;
    data: Order;
}

export interface CheckoutResponse {
    order_id: string;
    session_id: string;
    session_url: string;
}

// Query Parameter Interfaces
export interface OrdersQueryParams {
    page?: number;
    limit?: number;
    status?: OrderStatus;
    payment_status?: PaymentStatus;
    type?: OrderType;
    startDate?: string;
    endDate?: string;
}

export interface CartItem {
    id: string;
    item_id: string;
    item_type: ItemType;
    name: string;
    description: string;
    price: number;
    quantity: number;
    item: {
        base_price: number;
        description: string;
        id: string;
        name: string;
        thumbnail_url: string;
        type: ArtifactType;
    };
}

export interface Cart {
    id: string;
    items: CartItem[];
    total_amount: number;
    total_items: number;
    metadata: {
        discount_code?: string;
        discount_amount?: number;
    };
    currency: string;
}

export interface Category {
    id: string;
    name: string;
    slug: string;
    description: string;
    icon: string;
    imageUrl?: string;
    parentId?: string;
    order?: number;
    isActive: boolean;
    createdAt: Date;
    updatedAt: Date;
}

export interface FeaturedCategory {
    id: string;
    name: string;
    description: string;
    slug: string;
    artifacts: FeaturedArtifact[];
}

export interface FeaturedArtifact {
    id: string;
    name: string;
    description: string;
    type: ArtifactType;
    base_price: number;
    thumbnail_url: string;
}

export interface FeaturedPackage {
    id: string;
    name: string;
    description: string;
    base_price: number;
    type?: ArtifactType;
    thumbnail_url: string;
}

export interface IWishlistMetadata {
    price_at_add?: number;
    notes?: string;
}

export interface IWishlist {
    id: string;
    user_id: string;
    artifact_id?: string;
    package_id?: string;
    item_type: ItemType;
    added_at: Date;
    item: FeaturedArtifact | FeaturedPackage;
    metadata?: IWishlistMetadata;
    // created_at: Date;
    // updated_at: Date;
}

export interface IWishlistResponse {
    success: boolean;
    data: IWishlist[];
    message?: string;
}

export interface IAddToWishlistRequest {
    item_type: ItemType;
    item_id: string;
    metadata?: IWishlistMetadata;
}

export interface IRemoveFromWishlistParams {
    type: ItemType;
    itemId: string;
}

// ============================================================================
// SUPPORT SYSTEM ADMIN INTERFACES
// ============================================================================

export interface SystemHealth {
    status: 'healthy' | 'warning' | 'critical';
    uptime: number;
    responseTime: number;
    errorRate: number;
    activeConnections: number;
    lastUpdated: Date;
}

export interface AdminMetrics {
    totalTickets: number;
    openTickets: number;
    resolvedToday: number;
    activeAgents: number;
    totalAgents: number;
    slaComplianceRate: number;
    averageResolutionTime: number;
    customerSatisfaction: number;
    systemHealth: SystemHealth;
}

export interface DashboardFilters {
    dateRange: 'today' | 'week' | 'month' | 'quarter';
    categoryId: string | 'all';
    agentId: string | 'all';
    priority: string | 'all';
}

export interface CategoryManagementFilters {
    search: string;
    isActive: boolean | 'all';
    parentId: string | 'all';
    sortBy: 'name' | 'createdAt' | 'ticketCount';
    sortOrder: 'asc' | 'desc';
}

export interface CategoryFormData {
    name: string;
    description: string;
    color: string;
    icon: string;
    isActive: boolean;
    parentId?: string;
    sla?: {
        responseTime: number;
        resolutionTime: number;
        priority: string;
    };
}

export interface UserManagementFilters {
    search: string;
    role: string | 'all';
    isActive: boolean | 'all';
    department: string | 'all';
    sortBy: 'firstName' | 'lastName' | 'email' | 'createdAt' | 'lastLoginAt';
    sortOrder: 'asc' | 'desc';
}

export interface UserFormData {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    department?: string;
    skills?: string[];
    isActive: boolean;
    permissions: string[];
}

export interface AnalyticsFilters {
    dateRange: 'today' | 'week' | 'month' | 'quarter' | 'year';
    categoryId: string | 'all';
    agentId: string | 'all';
    groupBy: 'day' | 'week' | 'month' | 'category' | 'agent';
}

export interface AnalyticsData {
    labels: string[];
    datasets: {
        label: string;
        data: number[];
        backgroundColor: string;
        borderColor: string;
    }[];
}

export interface AnalyticsMetrics {
    totalTickets: number;
    resolvedTickets: number;
    averageResolutionTime: number;
    slaComplianceRate: number;
    customerSatisfaction: number;
    agentPerformance: {
        agentId: string;
        agentName: string;
        ticketsResolved: number;
        averageResolutionTime: number;
        customerSatisfaction: number;
    }[];
    categoryPerformance: {
        categoryId: string;
        categoryName: string;
        ticketCount: number;
        averageResolutionTime: number;
        slaComplianceRate: number;
    }[];
}

export interface SLAMonitoringFilters {
    slaStatus: 'all' | 'on_track' | 'warning' | 'breached' | 'completed';
    priority: string | 'all';
    categoryId: string | 'all';
    agentId: string | 'all';
    search: string;
}

export interface SLAAlert {
    id: string;
    ticketId: string;
    ticket: any;
    slaTracking: any;
    alertType: 'warning' | 'breach';
    severity: 'low' | 'medium' | 'high' | 'critical';
    message: string;
    createdAt: Date;
    acknowledged: boolean;
    acknowledgedBy?: string;
    acknowledgedAt?: Date;
}

export interface SLATracking {
    id: string;
    ticketId: string;
    ticket: any;
    sla: any;
    startTime: Date;
    deadline: Date;
    timeRemaining: number;
    isWarning: boolean;
    isBreached: boolean;
    isCompleted: boolean;
    lastUpdated: Date;
}

export interface SLAMetrics {
    totalTickets: number;
    onTrack: number;
    warning: number;
    breached: number;
    completed: number;
    complianceRate: number;
    averageResponseTime: number;
    averageResolutionTime: number;
    breachCount: number;
    escalationCount: number;
}

export interface SLAConfig {
    id: string;
    name: string;
    description: string;
    responseTime: number;
    resolutionTime: number;
    warningThreshold: number;
    escalationLevel: string;
    isActive: boolean;
    businessHours: {
        monday: { enabled: boolean; start: string; end: string };
        tuesday: { enabled: boolean; start: string; end: string };
        wednesday: { enabled: boolean; start: string; end: string };
        thursday: { enabled: boolean; start: string; end: string };
        friday: { enabled: boolean; start: string; end: string };
        saturday: { enabled: boolean; start: string; end: string };
        sunday: { enabled: boolean; start: string; end: string };
    };
    metadata: Record<string, any>;
    createdAt: Date;
    updatedAt: Date;
}

export interface EscalationLevel {
    SUPPORT_AGENT: 'support_agent';
    SUPPORT_LEAD: 'support_lead';
    SUPPORT_ADMIN: 'support_admin';
    ADMIN: 'admin';
}
